<style scoped>
  .meta {
    color: #777;
    font-size: .85em;
  }

  .meta-bit {
    color: #333;
    font-weight: bold;
  }

  .tech-label + .tech-label {
    margin-left: 10px;
  }
  a#txt:hover {
    color: #0056b3 !important;
    text-decoration: none;
  }
  .page-header {
    border-bottom: 1px solid #EEE;
    margin: 10px 0;
    padding-bottom: 0;
}

</style>

<template>
    <div class="container">
        <div class="page-header">
            <h1>
                UNPACME
                <img src="../assets/unpacme_logo_black.png" alt="HOME" class="d-inline-block align-top" width="50" height="50" >
            </h1>
            <h5>An automated malware unpacking service from OpenAnalysis</h5>
        </div>
        <br>
        <p class="meta">
        UNPACME is an automated malware unpacking service. Submissions to UNPACME are analyzed using a set of custom unpacking processes maintained by OpenAnalysis. These processes extract all encrypted or packed payloads from the submission and return a unique set of payloads to the user. In short, UNPACME automates the first step in your malware analysis process.
        </p>
        <br>
        <span class="meta-bit">What We Unpack</span>
        <p class="meta">
        UNPACME will only extract packed or encrypted Windows Portable Executable (PE) files that are embedded in the submission. Our unpacking services do not allow the submissions to make network connections, we do not download second stage payloads for droppers or downloaders.
        <br>
        <br>
        We are continuously developing and expanding our suite of unpacking processes to cover new and difficult packing techniques, however our public unpacking service is limited to extracting full PE payloads only. For access to our private research services, including shell code extraction, and automated de-obfuscation please <a id="txt" href="mailto:unpacme@openanalysis.net">Contact Us</a>.
        </p>
        <br>
        <span class="meta-bit">Free to Use</span>
        <p class="meta">
            UNPACME free for non-commercial use in accordance with our <a id="txt" href="/tos">Terms of Service</a>.
            <br>
            <br>
            All free accounts are rate limited and have a restricted maximum number of submissions each month. For access to a higher submission limit, and commercial use, please see our available <a id="txt" href="/plans">Plans</a>.
        </p>
        <br>
        <span class="meta-bit">API Access</span>
        <p class="meta">
            The UNPACME website is a front end for our unpacking services. We also provide direct API access to our services with on-demand scalability. Documentation for our API can be found <a id="txt" target="_blank" href="https://api.unpac.me/">here</a>.
        </p>
        <br>
        <div class="page-header">
            <h5>Frequently Asked Questions</h5>
        </div>
        <br>
        <span class="meta-bit">Why didn’t my submission unpack?</span>
        <p class="meta">
        Our unpacking services are limited to extracting full PE files only. We do not extract shell code or other types of injected code. We do not support network connectivity for downloading payloads. We do not unpack commercial software or games.
        <br>
        <br>
        Some submissions simply aren’t packed, however if you believe your submission was packed and it contained a full PE payload there may be other limitations with our unpacking services that did not permit the sample to be unpacked. In this case we encourage you to <a id="txt" href="mailto:unpacme@openanalysis.net">contact us</a>.
        </p>
        <br>
        <span class="meta-bit">What if there are multiple packed payloads in my submission?</span>
        <p class="meta">
        UNPACME will attempt to extract all packed payloads from a submission. In the case where there are multiple payloads or multiple stages leading to a final payload, a unique set of all payloads will be returned to the user.
        </p>
        <br>
        <span class="meta-bit">Do you unpack VMProtect? What about Themida?</span>
        <p class="meta">
        Both Themida and VMProtect are commercial packers and are most commonly used to pack games and other commercial software. It is rare that they are used to pack malware. For these reasons we do not automatically unpack VMProtect or Themida.
        </p>
        <br>
        <span class="meta-bit">Do you unpack games or commercial software?</span>
        <p class="meta">
        No, we only unpack malware. During our analysis process we apply heuristic filters to filter non-malware submissions. In some cases games, specifically anti-cheat game software, may pass these filters due to their use of malware-type techniques (ex. hooking, process injection). Repeated uploading of commercial software to UNPACME may result in the suspension and or termination of your account. 
        <br>
        <br>
        If you believe we have inadvertently unpacked your commercial software please <a id="txt" href="mailto:unpacme@openanalysis.net">contact us</a> and we will take steps to remove the unpacked results and filter this software in the future.
        </p>
        <br>
        <span class="meta-bit">Why is my unpacked payload corrupted, or unable to run?</span>
        <p class="meta">
        In order to cover as many packing techniques as possible UNPACME use multiple unpacking processes. Some of these processes are unable to reconstruct a working PE payload from the data they extract. However, all returned payloads should be loadable in a disassembler such as IDA, and be an accurate enough representation of the malware to allow static analysis.
        <br>
        <br>
        We are are continuously working to improve these processes so they deliver working PE files. You can help by flagging corrupted payloads for further analysis, a feature that is available to users who have registered for our private research services.
        </p>
        <br>
        <span class="meta-bit">Can you unpack shell code?</span>
        <p class="meta">
        No. Our unpacking services are limited to extracting full PE files only. We do not extract shell code or other types of injected code.
        </p>
        <br>
        <span class="meta-bit">Why can’t I submit x64 PE files, or other types of files like Word documents?</span>
        <p class="meta">
        Currently UNPACME will only extract packed or encrypted x86 Windows PE files. However, we are continuously working to support more file types in the near future!
        </p>
        <br>
        <span class="meta-bit">Why am I unable to submit files? What is my rate limit and quota?</span>
        <p class="meta">
        All free accounts on UNPACME are rate limited and have a restricted maximum number of submissions each month. Your monthly submission limit and remaining quota is available from your <a id="txt" href="/account">account</a>.
        For access to a higher submission limit, and commercial use, please see our available <a id="txt" href="/plans">Plans</a>.
        </p>
        <br>
        <br>
    </div>
</template>

<script>
export default {
    beforeCreate() {
        document.body.className = 'none';
    },
    metaInfo() {
        return {
            title: 'UnpacMe About',
            meta: [{ name: 'description', content: 'UnpacMe is an automated malware unpacking service. We provide malware analysis at Internet scale. Free for non-commercial use.' },
                { name: 'keywords', content: 'unpacking,unpacme,unpakme,unpackme,malware,malware analysis,analysis,automation,sandbox,oalabs,openanalysis,open analysis' }],
        };
    },
};
</script>



// WEBPACK FOOTER //
// src/components/About.vue